import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Card, Collapse, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Markdown } from '@mygpt/app/chat/components';
import { useChatStore } from '@mygpt/app/chat';
import { normalizeThinkTags, stripNoAnswerTag } from '@mygpt/app/utils/agent';
import { AskUser } from './AskUser';
import { McpUiFrame } from './McpUiFrame';
import { Loading } from './icon';

interface AgentMessageProps {
  content: string;
  isDigitalHuman?: boolean;
  botMessageTextColor?: string;
  /** 小部件自定义的品牌主色 / 回答气泡色，透传给 Markdown 给文件卡片配色 */
  accentColor?: string;
  botMessageColor?: string;
  isLastMessage?: boolean;
  onAskUserReply?: (answer: string) => void;
  botId?: string;
  /** 是否仍在流式生成中（来自外层 pending 状态），用于在各阶段持续显示 loading */
  pending?: boolean;
}

interface ParsedSection {
  type: 'ANSWER' | 'TOOL_CALL' | 'TOOL_RESPONSE' | 'THINK' | 'PREAMBLE';
  content: string;
}

/**
 * 解析 catalog_agent 返回的特殊格式文本
 * 格式示例：
 * [PREAMBLE] text (单行，以换行符为边界)
 * [THINK] text
 * [ANSWER] text
 * [TOOL_CALL] {json}
 * [TOOL_RESPONSE] text
 */
const parseCatalogAgentContent = (content: string): ParsedSection[] => {
  const sections: ParsedSection[] = [];

  // 先处理 PREAMBLE（单行，以换行符为边界）
  const preambleRegex = /\[PREAMBLE\]\n?([^\n]*)\n?/g;
  let preambleMatch;
  while ((preambleMatch = preambleRegex.exec(content)) !== null) {
    const preambleContent = preambleMatch[1].trim();
    if (preambleContent) {
      sections.push({
        type: 'PREAMBLE',
        content: preambleContent,
      });
    }
  }

  // 移除 PREAMBLE 后再解析其他标签
  const contentWithoutPreamble = content.replace(/\[PREAMBLE\]\n?[^\n]*\n?/g, '');

  // 解析其他标签（THINK、ANSWER、TOOL_CALL、TOOL_RESPONSE）
  const regex =
    /\[(THINK|ANSWER|TOOL_CALL|TOOL_RESPONSE)\]\s*([\s\S]*?)(?=\[(?:THINK|ANSWER|TOOL_CALL|TOOL_RESPONSE)\]|$)/g;

  let match;
  while ((match = regex.exec(contentWithoutPreamble)) !== null) {
    const type = match[1] as 'THINK' | 'ANSWER' | 'TOOL_CALL' | 'TOOL_RESPONSE';
    const sectionContent = match[2].trim();

    if (sectionContent) {
      sections.push({
        type,
        content: sectionContent,
      });
    }
  }

  return sections;
};

/**
 * 检测当前正处于哪个中间态（思考 / 工具调用 / 工具响应）
 * 逻辑：agent 内容是交错的（可能 answer 一段 → 调用工具 → 再 answer），
 * 因此以「最后一个出现的标签」判断当前阶段，而非「是否存在某标签」：
 * - 最后是 [TOOL_CALL]            → 正在调用工具
 * - 最后是 [TOOL_RESPONSE]        → 工具已返回，正在处理结果
 * - 最后是 [THINK]                → 正在思考
 * - 最后是 [ANSWER] 或无标签       → 正在生成答案文本 / 起步，非中间态（交由通用 loading 处理）
 */
const hasInProgressToolOperation = (
  content: string
): { inProgress: boolean; type?: 'call' | 'response' | 'thinking' } => {
  const tagRegex = /\[(THINK|ANSWER|TOOL_CALL|TOOL_RESPONSE)\]/g;
  let lastTag: string | null = null;
  let match;
  while ((match = tagRegex.exec(content)) !== null) {
    lastTag = match[1];
  }

  switch (lastTag) {
    case 'TOOL_CALL':
      return { inProgress: true, type: 'call' };
    case 'TOOL_RESPONSE':
      return { inProgress: true, type: 'response' };
    case 'THINK':
      return { inProgress: true, type: 'thinking' };
    default:
      // [ANSWER] 或无中间态标签：正在生成答案文本或刚起步
      return { inProgress: false };
  }
};

/**
 * 解析工具内容，提取工具名称和数据
 */
const parseToolContent = (content: string) => {
  const lines = content.trim().split('\n');
  let toolName = '';
  let toolData = content;

  if (lines.length > 0 && lines[0].trim()) {
    // 从第一行获取工具名称
    toolName = lines[0].trim();
    toolData = lines.slice(1).join('\n').trim() || content;

    // 仅当整段 toolData 本身就是一个 JSON 对象时才美化格式（典型 TOOL_CALL 的 args 场景）。
    // ⚠️ 不要在内容里贪婪挖 `{...}` —— 否则 TOOL_RESPONSE 中的 markdown 表格 / 代码块 / CSV 等
    // 会被其中一段示例 JSON 片段（比如 SKILL.md 里给 LLM 看的样例）整体覆盖，
    // 导致用户只能看到一截 JSON，原本的工具响应正文消失。
    const trimmed = toolData.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        toolData = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // 不是合法 JSON，保留原始格式
      }
    }
  }

  return { toolName, toolData };
};

/**
 * Detect MCP App payload: {type:"app", resourceUri, data, _meta}
 * Standard MCP Apps protocol (SEP-1865).
 */
const detectMcpApp = (text: string) => {
  if (!text || text[0] !== '{') return null;
  try {
    const parsed = JSON.parse(text);
    if (
      parsed &&
      parsed.type === 'app' &&
      typeof parsed.resourceUri === 'string' &&
      parsed.resourceUri.startsWith('ui://') &&
      parsed.data
    ) {
      return parsed;
    }
  } catch {
    // Not JSON
  }
  return null;
};

interface McpUiSegment {
  segType: 'mcpui';
  url?: string;
  srcdoc?: string;
  resourceUri?: string;
  appData?: Record<string, unknown>;
  width: string;
  height: string;
}

interface AskUserSegment {
  segType: 'ask_user';
  questions: { question: string; options?: (string | Record<string, unknown>)[]; multi_select?: boolean }[];
  askId: string;
}

interface SectionSegment {
  segType: 'section';
  section: ParsedSection;
}

type ProcessedSegment = McpUiSegment | AskUserSegment | SectionSegment;

/**
 * 将 ParsedSection[] 处理为 ProcessedSegment[]
 * 检测 ask_user / render_ui TOOL_CALL + TOOL_RESPONSE 组合，转换为 McpUiFrame / AskUser 渲染段
 */
const processUiSegments = (sections: ParsedSection[], isLastMessage: boolean): ProcessedSegment[] => {
  const result: ProcessedSegment[] = [];
  let segIndex = 0;

  for (const section of sections) {
    if (section.type === 'TOOL_CALL') {
      // Hide TOOL_CALL if it contains ui:// (UI tools rendered via TOOL_RESPONSE only)
      if (section.content.includes('"ui://')) continue;

      result.push({ segType: 'section', section });
    } else if (section.type === 'TOOL_RESPONSE') {
      const lines = section.content.trim().split('\n');
      const toolData = lines.slice(1).join('\n').trim();

      const mcpApp = detectMcpApp(toolData);
      if (mcpApp) {
        const meta = mcpApp._meta || {};
        const frameSize = meta['mcpui.dev/ui-preferred-frame-size'] as [string, string] | undefined;
        const width = frameSize?.[0] || '100%';
        const height = frameSize?.[1] || '400px';
        const resourceUri = mcpApp.resourceUri || '';
        const appData = mcpApp.data;

        if (resourceUri === 'ui://mcp-ui/ask-user' || resourceUri === 'ui://mcp-ui/ask_user') {
          // ask-user: questions data
          if (!isLastMessage) continue;
          if (appData && Array.isArray(appData.questions)) {
            result.push({
              segType: 'ask_user',
              questions: appData.questions as {
                question: string;
                options?: (string | Record<string, unknown>)[];
                multi_select?: boolean;
              }[],
              askId: `ask-user-${segIndex++}`,
            });
          }
        } else if (resourceUri === 'ui://mcp-ui/html' && appData?.html_content) {
          // Built-in HTML mode: data carries html_content directly
          result.push({ segType: 'mcpui', srcdoc: appData.html_content as string, width, height });
        } else if (resourceUri === 'ui://mcp-ui/url' && appData?.url) {
          // Built-in URL mode: embed external URL directly in iframe
          result.push({ segType: 'mcpui', url: appData.url as string, width, height });
        } else {
          // Standard MCP App: fetch resource HTML from API, pass data via postMessage
          result.push({ segType: 'mcpui', resourceUri, appData, width, height });
        }
      } else {
        result.push({ segType: 'section', section });
      }
    } else {
      result.push({ segType: 'section', section });
    }
  }

  return result;
};

interface ToolCardProps {
  content: string;
  index: number;
  type: 'call' | 'response';
}

/**
 * 通用工具卡片组件
 */
const ToolCard: React.FC<ToolCardProps> = ({ content, type }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const { toolName, toolData } = parseToolContent(content);

  // 根据类型确定样式和翻译键
  const config =
    type === 'call'
      ? {
          translationKey: 'catalog_agent.tool_call',
          fallbackName: t('catalog_agent.unknown_tool'),
          borderColor: 'border-violet-200',
          bgColor: 'bg-violet-50',
          textColor: 'violet.7' as const,
          borderColorInner: 'border-violet-100',
        }
      : {
          translationKey: 'catalog_agent.tool_response',
          fallbackName: t('catalog_agent.unknown_tool_response'),
          borderColor: 'border-purple-200',
          bgColor: 'bg-purple-50',
          textColor: 'grape.7' as const,
          borderColorInner: 'border-purple-100',
        };

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder className={`my-3 ${config.borderColor} ${config.bgColor}`}>
      <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => setOpened(!opened)}>
        <span
          className="transition-transform duration-200 text-gray-700"
          style={{ transform: opened ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        >
          ▼
        </span>
        <Text size="sm" fw={600} c={config.textColor}>
          {t(config.translationKey, { name: toolName || config.fallbackName })}
        </Text>
      </div>
      <Collapse in={opened}>
        {/*
          工具调用 / 响应内容用 <pre> 原始文本展示，刻意不复用问答区的 <Markdown>：
          - 工具响应里常含 `<CITATION ... />` / `<CITATION_INSTRUCTIONS>` 等字面文本
            （给 LLM 看的提示），交给问答 Markdown 会被当成真引用渲染成角标，破坏可读性。
          - 工具响应不是给用户精排的内容，保真展示比 markdown 排版更重要。
          - 长行通过 whitespace-pre-wrap + break-words 自动折行，避免横向溢出。
        */}
        <pre
          className={`mt-3 p-3 bg-white rounded border ${config.borderColorInner} text-xs overflow-x-auto whitespace-pre-wrap break-words`}
        >
          <code>{toolData}</code>
        </pre>
      </Collapse>
    </Card>
  );
};

/**
 * 工具调用卡片组件
 */
const ToolCallCard: React.FC<{ content: string; index: number }> = ({ content, index }) => {
  return <ToolCard content={content} index={index} type="call" />;
};

/**
 * 工具响应卡片组件
 */
const ToolResponseCard: React.FC<{ content: string; index: number }> = ({ content, index }) => {
  return <ToolCard content={content} index={index} type="response" />;
};

/**
 * 思考指示器
 */
const ThinkingIndicator: React.FC<{ isDigitalHuman?: boolean }> = ({ isDigitalHuman }) => {
  const { t } = useTranslation();

  // 数字人模式背景为深色，圆点与文字统一改为白色，避免在深色背景上看不清
  const dotColor = isDigitalHuman ? 'bg-white' : 'bg-violet-400';
  const textColor = isDigitalHuman ? 'text-white' : 'text-gray-600';

  return (
    <>
      <div className="flex items-center gap-2 my-3">
        <span className="relative flex h-2 w-2">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-75`}
          ></span>
          <span className={`relative inline-flex rounded-full h-2 w-2 ${dotColor}`}></span>
        </span>
        <span className={`text-sm ${textColor} animate-pulse`}>{t('agent.thinking')}</span>
      </div>
    </>
  );
};

/**
 * 工具操作加载指示器
 */
const ToolLoadingIndicator: React.FC<{ isDigitalHuman?: boolean }> = ({ isDigitalHuman }) => {
  const { t } = useTranslation();

  // 数字人模式背景为深色：齿轮反色为白色（中心镂空显紫），文字改白色
  const gearColor = isDigitalHuman ? '#ffffff' : '#7c3aed';
  const gearCenterColor = isDigitalHuman ? '#7c3aed' : '#ffffff';
  const textColor = isDigitalHuman ? 'text-white' : 'text-gray-600';

  return (
    <>
      <style>{`
        @keyframes tool-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes tool-pulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }
      `}</style>
      <div className="flex items-center gap-2 my-3">
        {/* 齿轮旋转动画 */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            animation: 'tool-spin 2s linear infinite',
          }}
        >
          <path
            d="M12 2C12.5523 2 13 2.44772 13 3V4.5C13.6667 4.6 14.3 4.8 14.9 5.1L16 3.9C16.3905 3.50948 17.0237 3.50948 17.4142 3.9L18.5 5C18.8905 5.39052 18.8905 6.02369 18.5 6.41421L17.3 7.5C17.6 8.1 17.8 8.73333 17.9 9.4H19.5C20.0523 9.4 20.5 9.84772 20.5 10.4V11.6C20.5 12.1523 20.0523 12.6 19.5 12.6H17.9C17.8 13.2667 17.6 13.9 17.3 14.5L18.5 15.6C18.8905 15.9905 18.8905 16.6237 18.5 17.0142L17.4142 18.1C17.0237 18.4905 16.3905 18.4905 16 18.1L14.9 16.9C14.3 17.2 13.6667 17.4 13 17.5V19C13 19.5523 12.5523 20 12 20C11.4477 20 11 19.5523 11 19V17.5C10.3333 17.4 9.7 17.2 9.1 16.9L8 18.1C7.60948 18.4905 6.97631 18.4905 6.58579 18.1L5.5 17.0142C5.10948 16.6237 5.10948 15.9905 5.5 15.6L6.7 14.5C6.4 13.9 6.2 13.2667 6.1 12.6H4.5C3.94772 12.6 3.5 12.1523 3.5 11.6V10.4C3.5 9.84772 3.94772 9.4 4.5 9.4H6.1C6.2 8.73333 6.4 8.1 6.7 7.5L5.5 6.41421C5.10948 6.02369 5.10948 5.39052 5.5 5L6.58579 3.9C6.97631 3.50948 7.60948 3.50948 8 3.9L9.1 5.1C9.7 4.8 10.3333 4.6 11 4.5V3C11 2.44772 11.4477 2 12 2Z"
            fill={gearColor}
            fillOpacity="0.8"
          />
          <circle cx="12" cy="12" r="3" fill={gearCenterColor} />
        </svg>
        <span className={`text-sm ${textColor} animate-pulse`}>{t('agent.calling_tool_default')}</span>
      </div>
    </>
  );
};

/**
 * AgentMessage 组件
 * 用于渲染 catalog_agent 类型的消息
 */
export const AgentMessage: React.FC<AgentMessageProps> = ({
  content,
  isDigitalHuman,
  botMessageTextColor,
  accentColor,
  botMessageColor,
  isLastMessage = false,
  onAskUserReply,
  botId,
  pending = false,
}) => {
  const { t } = useTranslation();
  const { showToolDetails } = useChatStore();

  // 将 <think>/<thinking> 标签转换为 [THINK] 格式，统一由 parseCatalogAgentContent 解析
  // 同时复用 stripNoAnswerTag 移除 <NO_ANSWER> 哨兵标记（不应展示给用户）
  const cleanedContent = useMemo(() => stripNoAnswerTag(normalizeThinkTags(content)), [content]);

  // 使用 ref 缓存提取到的 PREAMBLE 内容，即使后续 content 中的 PREAMBLE 被删除也能保留
  const cachedPreambleRef = useRef<string | null>(null);

  // 从 content 中提取并缓存 PREAMBLE（流式输出时可能先出现 [PREAMBLE]，后续被清理）
  useEffect(() => {
    // 检测是否有 PREAMBLE 标签（包括可能跨行的情况）
    const preambleMatch = cleanedContent.match(/\[PREAMBLE\]\n?([^\n]*)/);
    if (preambleMatch && preambleMatch[1]) {
      const extractedPreamble = preambleMatch[1].trim();
      if (extractedPreamble) {
        cachedPreambleRef.current = extractedPreamble;
      }
    }
  }, [cleanedContent]);

  const sections = parseCatalogAgentContent(cleanedContent);

  // 检测是否有正在进行中的 tool 操作
  const toolOperationStatus = useMemo(() => hasInProgressToolOperation(cleanedContent), [cleanedContent]);

  // 检查是否存在 ANSWER，如果存在则不显示 PREAMBLE
  const hasAnswer = useMemo(() => sections.some((section) => section.type === 'ANSWER'), [sections]);

  // 提取 PREAMBLE 内容（如果存在且没有 ANSWER）
  // 优先使用缓存的 preamble，然后从 sections 解析
  const preambleContent = useMemo(() => {
    if (hasAnswer || isDigitalHuman) return null;

    // 优先使用缓存的 preamble
    if (cachedPreambleRef.current) return cachedPreambleRef.current;

    const preambleSection = sections.find((section) => section.type === 'PREAMBLE');
    return preambleSection?.content || null;
  }, [sections, hasAnswer, isDigitalHuman]);

  // loading 指示器：仅在流式生成中（pending）时显示，统一渲染在已生成内容的下方，生成结束后自动消失
  // - 思考阶段：思考指示器；工具调用 / 响应阶段：工具加载指示器
  // - 生成答案文本或刚起步（最后一个标签为 [ANSWER] 或尚无标签）：通用 spinner
  const loadingIndicator = !pending ? null : toolOperationStatus.inProgress ? (
    toolOperationStatus.type === 'thinking' ? (
      <ThinkingIndicator isDigitalHuman={isDigitalHuman} />
    ) : (
      <ToolLoadingIndicator isDigitalHuman={isDigitalHuman} />
    )
  ) : (
    <Loading color={isDigitalHuman ? '#ffffff' : undefined} />
  );

  // 将 sections 处理为包含 UI 段的 ProcessedSegment[]
  // 必须位于下方提前 return 之前：sections 每次渲染重新计算，流式输出期间
  // 同一实例会先后走"无标签"与"有标签"两条分支，Hook 顺序随分支变化会触发
  // React "Rendered more hooks than during the previous render" 并导致白屏 (#3209)。
  const processedSegments = useMemo(() => processUiSegments(sections, isLastMessage), [sections, isLastMessage]);

  // 如果没有解析到任何标签，降级为普通 Markdown 渲染
  if (sections.length === 0) {
    return (
      <>
        {/* 显示垫语 */}
        {preambleContent && <div className="text-sm text-gray-600 mb-2">{preambleContent}</div>}
        <Markdown
          className="text-[14px]"
          content={cleanedContent}
          footnotes={[]}
          referenceList={[]}
          referenceIndexes={[]}
          isDigitalHuman={isDigitalHuman}
          botMessageTextColor={botMessageTextColor}
          accentColor={accentColor}
          botMessageColor={botMessageColor}
        />
        {/* 流式生成中的 loading 指示器（思考 / 工具调用 / 生成答案） */}
        {loadingIndicator}
      </>
    );
  }

  return (
    <div className="catalog-agent-message">
      {/* 显示垫语 */}
      {preambleContent && <div className="text-sm text-gray-600 mb-2">{preambleContent}</div>}
      {processedSegments.map((seg, index) => {
        // MCP UI iframe 渲染
        if (seg.segType === 'mcpui') {
          return (
            <McpUiFrame
              key={`mcpui-${index}`}
              url={seg.url}
              srcdoc={seg.srcdoc}
              resourceUri={seg.resourceUri}
              appData={seg.appData}
              botId={botId}
              width={seg.width}
              height={seg.height}
              onAppResponse={(payload) => {
                if (onAskUserReply) {
                  const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
                  onAskUserReply(text);
                }
              }}
            />
          );
        }

        // ask_user 交互式问答渲染
        if (seg.segType === 'ask_user') {
          return (
            <AskUser
              key={`ask-${seg.askId}`}
              questions={seg.questions}
              askId={seg.askId}
              onReply={(answer) => onAskUserReply?.(answer)}
            />
          );
        }

        // 原有 section 渲染逻辑
        const section = seg.section;
        switch (section.type) {
          case 'PREAMBLE':
            return null;

          case 'THINK':
            if (isDigitalHuman) return null;
            if (!showToolDetails) return null;
            return (
              <div key={index} className="my-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="text-sm text-yellow-700 mb-2 font-medium flex items-center gap-2">
                  🤔{t('agent.thinking')}
                </div>
                {section.content && (
                  <Markdown
                    className="text-xs text-yellow-600"
                    content={section.content}
                    footnotes={[]}
                    referenceList={[]}
                    referenceIndexes={[]}
                    isDigitalHuman={isDigitalHuman}
                    botMessageTextColor={botMessageTextColor}
                    accentColor={accentColor}
                    botMessageColor={botMessageColor}
                  />
                )}
              </div>
            );

          case 'ANSWER':
            return (
              <div key={index} className="my-2">
                <Markdown
                  className="text-[14px]"
                  content={section.content}
                  footnotes={[]}
                  referenceList={[]}
                  referenceIndexes={[]}
                  isDigitalHuman={isDigitalHuman}
                  botMessageTextColor={botMessageTextColor}
                  accentColor={accentColor}
                  botMessageColor={botMessageColor}
                />
              </div>
            );

          case 'TOOL_CALL':
            if (isDigitalHuman) return null;
            if (!showToolDetails) return null;
            return <ToolCallCard key={index} content={section.content} index={index} />;

          case 'TOOL_RESPONSE':
            if (isDigitalHuman) return null;
            if (!showToolDetails) return null;
            return <ToolResponseCard key={index} content={section.content} index={index} />;

          default:
            return null;
        }
      })}
      {/* 流式生成中的 loading 指示器（思考 / 工具调用 / 生成答案） */}
      {loadingIndicator}
    </div>
  );
};
